import React, { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import Button from '../../../buttons/Button';
import { calculateAmountToPick, generatePickID, mergeClassNames } from '../../../../utils';
import { getTotalPickedQuantity } from '../../../../utils/pickHelpers';

import { updatePickTourItemsAndExit, updatePickTour } from '../../../../redux/actions/activePickTour.actions';

import { UpdateType, UpdateStatus } from '../../../../redux/actions/activePickTour.types';

import styles from './ExitPickTour.css';
import useInactivePickTourKickout from '../../../../hooks/useInactivePickTourKickout';
import { getConfigAuthValuesFromState } from '../../../../redux/selectors';
import { LANGUAGE_SPANISH, ORDER_TYPE_COMMERCIAL } from '../../../../constants';
import { PaginationData, PickTour } from '../../../../types';
import { RootState } from '../../../../redux/reducers/rootReducer';
import { APIPickTourListItem } from '../../../../redux/sagas/types';

type Props = {
  determinePickTourStatus: (updateType: UpdateType) => UpdateStatus;
  currentLanguage: string;
  pickTour: PickTour;
  paginationData: PaginationData;
};

const ExitPickTour = ({
  determinePickTourStatus,
  currentLanguage,
  pickTour,
  paginationData
}: Props): ReactElement => {
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const pickTourList = useSelector((state: RootState) => state.activePickTour?.data?.pickTourList || []);
  const { t } = useTranslation();
  const translationRoot = 'PICK_TOUR_COMPLETE_OR_EXIT';
  const INACTIVITY_TIMEOUT = 1000 * 60 * (configAuthValues?.INACTIVITY_TIMEOUT || 10);
  const dispatch = useDispatch();

  useInactivePickTourKickout(INACTIVITY_TIMEOUT)

  const handleExitPickTourCommercial = () => {
    // Collect all item updates that need to be processed
    const itemUpdates = pickTour.picks
      .map((pick, index) => {
        const pickID = generatePickID(pick);
        const persistedPickItem = pickTourList.find(
          (item: APIPickTourListItem) =>
            `${item.orderType}:${item.orderID}:${item.pickTourItemDetailLineNumber}` === pickID
        );

        // Calculate new picked quantity from current session (from locations)
        const newPicksFromLocations = getTotalPickedQuantity(pick.locations);

        // Calculate total picked quantity (previous + new) for validation
        const totalPickedFromLocations = (persistedPickItem?.pickedItemQuantity || 0) + newPicksFromLocations;

        if (
          pick.isEligibleForPicking &&
          !pick.isSkipped &&
          newPicksFromLocations > 0 &&
          totalPickedFromLocations < calculateAmountToPick(pick)
        ) {
          return {
            pickID,
            pickedQuantity: newPicksFromLocations, // Send DELTA (new picks only) to API
            skipReason: null,
            isSkipped: false,
            locationIndex: pick.locationIndex,
            nextPick: pick,
            isLastPick: index === pickTour.picks.length - 1,
          };
        }
        return null;
      })
      .filter((update): update is NonNullable<typeof update> => update !== null);

    // Dispatch a single action that batches all updates and then exits
    dispatch(
      updatePickTourItemsAndExit({
        itemUpdates,
        paginationData,
        updateStatus: determinePickTourStatus('exit'),
      })
    );
  };

  const handleExitPickTour = () => {
    dispatch(updatePickTour({ updateType: 'exit', updateStatus: determinePickTourStatus('exit') }));
  };

  const orderType = pickTour.picks[0]?.orderType;
  const onExitClick = orderType === ORDER_TYPE_COMMERCIAL ? handleExitPickTourCommercial : handleExitPickTour;

  return (
    <section className={styles.section}>
      <h1 className={mergeClassNames(styles.h1, currentLanguage === LANGUAGE_SPANISH && styles.h1Spanish)}>
        {t(`${translationRoot}.EXIT_PICK_TOUR`)}
      </h1>
      <p className={styles.p}>{t(`${translationRoot}.EXIT_PICK_TOUR_CONFIRMATION`)}</p>
      <Button
        className={styles.button}
        onClick={onExitClick}
        data-cy="pick-tour-confirm-exit-button"
      >
        {t(`${translationRoot}.EXIT_PICK_TOUR`)}
      </Button>
    </section>
  );
};

export default ExitPickTour;
